import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '../auth/authStore';
import { useNewAssignmentStore } from '../orders/newAssignmentStore';
import { upsertPushSubscription } from './pushApi';
import { getVapidPublicKey, isPushSupported, pushLogger, urlBase64ToUint8Array } from './pushSupport';
import { usePushStore } from './pushStore';

export function usePushPermission(): void {
  const userId = useAuthStore((state) => state.user?.id);
  const newCount = useNewAssignmentStore((state) => state.newCount);
  const status = usePushStore((state) => state.status);
  const hasPrompted = usePushStore((state) => state.hasPrompted);
  const setStatus = usePushStore((state) => state.setStatus);
  const setSubscription = usePushStore((state) => state.setSubscription);
  const markPrompted = usePushStore((state) => state.markPrompted);

  const subscribingRef = useRef(false);

  const subscribeAndSave = useCallback(
    async (targetUserId: string): Promise<void> => {
      const vapidPublicKey = await getVapidPublicKey();
      if (!vapidPublicKey) {
        pushLogger.warn('VAPID public key unavailable – push skipped');
        return;
      }

      try {
        const registration = await navigator.serviceWorker.ready;
        const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          });
        }

        await upsertPushSubscription(targetUserId, subscription);
        setSubscription(subscription);
        setStatus('subscribed');
      } catch (error) {
        pushLogger.error('subscribeAndSave failed', error);
      }
    },
    [setStatus, setSubscription],
  );

  const requestAndSubscribe = useCallback(
    async (targetUserId: string): Promise<void> => {
      if (subscribingRef.current) return;
      subscribingRef.current = true;

      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setStatus('denied');
          return;
        }

        await subscribeAndSave(targetUserId);
      } catch (error) {
        pushLogger.error('requestAndSubscribe failed', error);
      } finally {
        subscribingRef.current = false;
      }
    },
    [setStatus, subscribeAndSave],
  );

  useEffect(() => {
    if (!userId || status !== 'idle') return;

    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }

    const permission = Notification.permission;
    if (permission === 'denied') {
      setStatus('denied');
      return;
    }

    if (permission === 'granted') {
      void subscribeAndSave(userId);
      return;
    }

    setStatus('not_subscribed');
  }, [setStatus, status, subscribeAndSave, userId]);

  useEffect(() => {
    if (!userId || status !== 'not_subscribed' || newCount === 0 || hasPrompted) return;

    // 1.31.0 — Запрашиваем разрешение ТОЛЬКО в установленном PWA.
    // В обычной вкладке браузера запрос бессмыслен: после установки PWA
    // на Android Chrome создаёт отдельный контекст хранения, и
    // разрешение приходится давать заново ВНУТРИ установленного
    // приложения. Получалось два диалога подряд: один в браузере перед
    // установкой, второй сразу после открытия PWA. Теперь спрашиваем
    // один раз — в установленном PWA, когда это реально работает.
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari ставит свой флаг на navigator
      (window.navigator as { standalone?: boolean }).standalone === true;
    if (!isStandalone) return;

    const timeoutId = window.setTimeout(() => {
      markPrompted();
      void requestAndSubscribe(userId);
    }, 1500);

    return () => window.clearTimeout(timeoutId);
  }, [hasPrompted, markPrompted, newCount, requestAndSubscribe, status, userId]);
}
